import { useState, useEffect, useCallback } from 'react';

/**
 * 页面隐藏：2 分钟强制静音，5 分钟静默清空。
 * 保持原 empty-deps 可见性监听写法（含闭包语义）。
 */
export default function useMultiWindowVisibility({
  streamsRef,
  clearAllStreamsSilently
}) {
  const [isPageVisible, setIsPageVisible] = useState(true);
  const [pageHiddenTime, setPageHiddenTime] = useState(null);
  const [muteTimer, setMuteTimer] = useState(null);
  const [removeTimer, setRemoveTimer] = useState(null);
  const [streamStates, setStreamStates] = useState(new Map()); // 存储每个流的状态

  // 页面可见性变化处理
  const handleVisibilityChange = useCallback(() => {
    const isVisible = !document.hidden;
    console.log(`多窗口直播页面可见性变化: ${isVisible ? '可见' : '不可见'}`);
    console.log(`当前活跃流数量: ${streamsRef.current.filter(s => s !== null).length}`);

    if (isVisible) {
      // 页面变为可见
      setIsPageVisible(true);
      setPageHiddenTime(null);

      // 清除所有定时器
      if (muteTimer) {
        clearTimeout(muteTimer);
        setMuteTimer(null);
        console.log('已清除静音定时器');
      }
      if (removeTimer) {
        clearTimeout(removeTimer);
        setRemoveTimer(null);
        console.log('已清除移除定时器');
      }

      // 恢复所有流的正常状态
      setStreamStates(prev => {
        const newStates = new Map(prev);
        let hasChanges = false;

        for (const [streamId, state] of newStates.entries()) {
          if (state.isMuted) {
            newStates.set(streamId, { ...state, isMuted: false });
            hasChanges = true;
            console.log(`恢复流 ${streamId} 的正常播放状态`);
          }
        }

        return hasChanges ? newStates : prev;
      });

    } else {
      // 页面变为不可见
      setIsPageVisible(false);
      setPageHiddenTime(Date.now());
      console.log(`页面隐藏时间: ${new Date().toLocaleTimeString()}`);

      // 设置2分钟后静音
      const muteTimeout = setTimeout(() => {
        console.log('页面不可见2分钟，开始静音所有流');
        setStreamStates(prev => {
          const newStates = new Map(prev);
          let hasChanges = false;

          // 获取当前的streams状态
          const currentStreams = streamsRef.current; // 这里会获取最新的streams状态
          console.log(`准备静音的流数量: ${currentStreams.filter(s => s !== null).length}`);

          currentStreams.forEach((stream) => {
            if (stream) {
              const currentState = newStates.get(stream.id) || {};
              if (!currentState.isMuted) {
                newStates.set(stream.id, { ...currentState, isMuted: true });
                hasChanges = true;
                console.log(`流 ${stream.streamKey} 已设置为静音状态`);
              }
            }
          });

          return hasChanges ? newStates : prev;
        });
      }, 2 * 60 * 1000); // 2分钟
      setMuteTimer(muteTimeout);
      console.log('已设置2分钟静音定时器');

      // 设置5分钟后移除
      const removeTimeout = setTimeout(() => {
        console.log('页面不可见5分钟，开始移除所有流');
        clearAllStreamsSilently();
      }, 5 * 60 * 1000); // 5分钟
      setRemoveTimer(removeTimeout);
      console.log('已设置5分钟移除定时器');
    }
  }, []); // 移除所有依赖项，避免无限循环

  // 页面可见性监听
  useEffect(() => {
    console.log('设置页面可见性监听器');
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      console.log('清理页面可见性监听器');
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // 清理定时器
      if (muteTimer) {
        clearTimeout(muteTimer);
        console.log('清理静音定时器');
      }
      if (removeTimer) {
        clearTimeout(removeTimer);
        console.log('清理移除定时器');
      }
    };
  }, []); // 只在组件挂载时设置监听器

  return {
    isPageVisible,
    pageHiddenTime,
    muteTimer,
    removeTimer,
    streamStates,
    setStreamStates
  };
}
